import React from "react";
import { InsightsOutlined, WorkspacePremiumOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { PALETTE as P, CARD, NUM, TYPE, SPACE, tileRow } from "./shared/tokens";
import * as C from "./shared/content";
import {
  Card, CountUp, Delta, Fill, Legend, RankedBarList, HeatStrip, LoadingView, stats,
  EmptyState,
} from "./shared/primitives";
import CampaignTable from "../components/CampaignTable";

import WidgetGate from "../components/WidgetGate";
import { ALL_VISIBLE, WIDGET, WIDGET_LABELS } from "../layout/catalog";
import { DragGrid, DragItem, cardGrid, shownCount } from "../dnd/WidgetGridDnd";
import { platformBlocks, PLATFORM_FULL_WIDTH } from "./shared/PlatformBlocks";
// C · Editorial.
//
// The most air of the three. The four metrics become ONE band divided into
// columns rather than four separate cards, the page title is the largest type
// on the screen, and every trend is a strip of cells — one per day, shaded by
// volume — instead of a line.
//
// The cell strip is the argument this direction is making: a line drawn through
// mostly-zero days implies a continuum that is not there, while a row of
// squares shows a month as it actually is, a few busy days among quiet ones.
const Waffle = ({ legend, total, colours }) => {
  // One square per startup, capped so a large account cannot put thousands of
  // nodes on the page.
  const cells = Math.min(total, 400);
  const paid = legend.find((l) => l.key === "paid")?.value ?? 0;
  const earlier = legend.find((l) => l.key === "paid_earlier")?.value ?? 0;
  const squares = Array.from({ length: cells }, (_, i) =>
    i < paid ? colours.paid : i < paid + earlier ? colours.paid_earlier : colours.free,
  );

  return (
    <Box
      role="img"
      aria-label={legend.map((l) => `${l.label} ${l.value}`).join(", ")}
      // As many 16px squares per row as the card fits: a wide, short block on
      // desktop rather than a 10-column strip ~800px tall at the 400-square cap.
      sx={{ width: "100%", display: "grid", gridTemplateColumns: "repeat(auto-fill, 16px)", gap: "4px" }}
    >
      {squares.map((c, i) => (
        <Box
          key={i}
          className="dv-pop"
          style={{ animationDelay: `${(0.6 + (i % 10) * 0.02 + Math.floor(i / 10) * 0.03).toFixed(3)}s` }}
          sx={{ width: 16, height: 16, borderRadius: "4px", background: c }}
        />
      ))}
      {cells === 0 && <Typography sx={{ fontSize: TYPE.label, color: P.inkMuted }}>No startups in scope.</Typography>}
    </Box>
  );
};

const DashboardC = ({ data, monetization, platform, platformState = {}, scope, isLoading, view = ALL_VISIBLE, onOrderChange = () => {}, onItemOrderChange = () => {} }) => {
  const shell = {
    background: P.ground,
    borderRadius: "16px",
    // padding: { xs: SPACE.lg, md: SPACE.sm },
    display: "flex",
    flexDirection: "column",
    gap: SPACE.xl,
  };

  if (isLoading) {
    return (
      <Box sx={shell}>
        <LoadingView />
      </Box>
    );
  }

  const series = C.seriesFrom(data?.daily);
  const metrics = C.metrics(data, monetization);
  const pipe = C.pipeline(data);
  const plan = C.planMix(monetization);
  const assess = C.assessments(monetization);
  const comp = C.composition(data);
  const camp = C.campaigns(data);

  const planColours = { paid: P.accent, paid_earlier: P.pipeline.interested, free: P.pipeline.invited };
  // Lightest to deepest along one hue, so the stages read as a progression.
  const stageTile = {
    invited: { bg: P.accentSoft, fg: P.ink },
    interested: { bg: P.iconChip, fg: P.ink },
    invested: { bg: P.pipeline.invited, fg: P.ink },
    rejected: { bg: P.surface, fg: P.inkMuted, outlined: true },
  };

    const blocks = {
      key_metrics: (
      <Box
        component="section"
        aria-label="Key metrics"
        className="dv-card"
        style={{ animationDelay: ".1s" }}
        sx={{ ...CARD, padding: 0, overflow: "hidden", ...tileRow(240, 0, shownCount(view, "key_metrics", metrics)) }}
      >
        <DragGrid {...cardGrid(view, "key_metrics", metrics, onItemOrderChange)}>{view.sortItems("key_metrics", metrics).map((m, i) => (<WidgetGate key={m.key} fill show={view.item("key_metrics", m.key)}><DragItem id={m.key} label={m.label ?? m.title} radius="14px">
          <Box
            key={m.key}
            className="dv-item"
            style={{ animationDelay: `${0.15 + i * 0.065}s` }}
            sx={{
              padding: "22px 24px",
              display: "flex",
              flexDirection: "column",
              gap: SPACE.sm,
              height: "100%",
              boxShadow: `-1px 0 0 ${P.line}, 0 -1px 0 ${P.line}`,
            }}
          >
            <Typography sx={{ fontSize: TYPE.label, color: P.inkMuted, fontWeight: 500 }}>{m.label}</Typography>
            <Box display="flex" alignItems="baseline" gap={SPACE.sm}>
              <CountUp value={m.value} sx={{ fontSize: "2.5rem", fontWeight: 600, letterSpacing: "-0.04em", lineHeight: 1, color: P.ink }} />
              <Delta value={m.delta} higherIsBetter={m.higherIsBetter} />
            </Box>
            <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{m.footnote}</Typography>
            {m.series && (
              <Box sx={{ marginTop: 0.5 }}>
                <HeatStrip values={series[m.series]} label={`${m.label} by day`} delay={0.45 + i * 0.05} cellHeight={10} />
              </Box>
            )}
          </Box>
        </DragItem></WidgetGate>))}</DragGrid>
      </Box>
      ),
      pipeline: (
      <Card title="Pipeline" subtitle={pipe.subtitle} hint="Every invitation sent in this range, grouped by the status it holds right now. The four states are disjoint and add up to the total." delay={0.25} sx={{ padding: { xs: "16px", sm: "24px" }, gap: SPACE.lg }}>
        <Box sx={tileRow(180, SPACE.md, pipe.stages.length, "& > *")}>
          {pipe.stages.map((s, i) => {
            const tone = stageTile[s.key];
            return (
              <Box
                key={s.key}
                className="dv-pop"
                style={{ animationDelay: `${0.4 + i * 0.07}s` }}
                sx={{
                  padding: "18px",
                  borderRadius: "14px",
                  background: tone.bg,
                  color: tone.fg,
                  border: tone.outlined ? `1px solid ${P.line}` : "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: SPACE.xs,
                }}
              >
                <Typography sx={{ fontSize: TYPE.label, color: "inherit", opacity: tone.outlined ? 1 : 0.9 }}>{s.label}</Typography>
                <CountUp value={s.n} sx={{ fontSize: "2rem", fontWeight: 600, letterSpacing: "-0.03em", color: "inherit" }} />
              </Box>
            );
          })}
        </Box>

        <Box
          role="img"
          aria-label={`${pipe.total} invitations: ${pipe.stages.map((s) => `${s.n} ${s.label}`).join(", ")}`}
          sx={{ display: "flex", height: 10, borderRadius: "6px", overflow: "hidden", gap: "2px" }}
        >
          {pipe.stages
            .filter((s) => s.n > 0)
            .map((s, i) => (
              <Box key={s.key} sx={{ width: `${(s.n / (pipe.total || 1)) * 100}%`, overflow: "hidden" }}>
                <Box className="dv-fill" style={{ animationDelay: `${0.7 + i * 0.05}s`, transform: "scaleX(1)" }} sx={{ height: "100%", background: P.pipeline[s.key] }} />
              </Box>
            ))}
          {pipe.total === 0 && <Box sx={{ flex: 1, background: P.track }} />}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: SPACE.md, borderTop: `1px solid ${P.line}`, paddingTop: SPACE.md }}>
          {pipe.rates.map((r, i) => (
            <Box key={r.label} className="dv-item" style={{ animationDelay: `${0.8 + i * 0.04}s` }}>
              <Typography sx={{ display: "block", fontSize: TYPE.micro, color: P.inkMuted }}>{r.label}</Typography>
              <Typography sx={{ ...NUM, fontSize: "1.4rem", fontWeight: 600, color: P.ink }}>{r.value}</Typography>
            </Box>
          ))}
        </Box>
      </Card>
      ),
      daily_activity: (
      <Card title="Daily Activity" subtitle={C.activity.subtitle} hint="Each panel is scaled to its own series. One cell per day, shaded by how much happened, so a quiet day is visibly quiet." delay={0.35} sx={{ padding: { xs: "16px", sm: "24px" }, gap: SPACE.lg }}>
        {C.activity.panels.map((panel, i) => {
          const st = stats(series[panel.key]);
          return (
            <Box key={panel.key} sx={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
              <Box display="flex" flexWrap="wrap" justifyContent="space-between" gap={SPACE.sm}>
                <Typography sx={{ display: "inline-flex", alignItems: "center", gap: SPACE.sm, fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>
                  <Box component="span" sx={{ width: 9, height: 9, borderRadius: "3px", background: P.series[i] }} />
                  {panel.name}
                </Typography>
                <Box display="inline-flex" gap={0.75}>
                  {[`total ${st.total}`, `peak ${st.peak}`].map((chip) => (
                    <Typography key={chip} component="span" sx={{ ...NUM, padding: "2px 8px", borderRadius: "6px", background: P.track, fontSize: TYPE.micro, color: P.inkMuted }}>
                      {chip}
                    </Typography>
                  ))}
                </Box>
              </Box>
              <HeatStrip values={series[panel.key]} label={`${panel.name}: total ${st.total}, peak ${st.peak}`} delay={0.5 + i * 0.1} />
            </Box>
          );
        })}
      </Card>
      ),
      plan_mix: (
        <Card
          title="Plan Mix"
          subtitle={plan.subtitle}
          hint="One square per startup. A startup with no plan is a free (lite) user; one that bought a plan inside the range is paid; anyone already paying before it is counted separately."
          delay={0.45}
          sx={{ padding: { xs: "16px", sm: "24px" }, gap: SPACE.lg }}
          right={<Legend keys={plan.legend.map((l) => ({ label: l.label, color: planColours[l.key] }))} />}
        >
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={SPACE.xl}>
            <Waffle legend={plan.legend} total={plan.total} colours={planColours} />
            <Box sx={{ flex: 1, minWidth: 180, display: "flex", flexDirection: "column", gap: SPACE.sm }}>
              {plan.legend.map((l) => (
                <Box key={l.key} display="flex" justifyContent="space-between">
                  <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft }}>{l.label}</Typography>
                  <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{l.value}</Typography>
                </Box>
              ))}
              <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{plan.footnote}</Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: `1px solid ${P.line}`, paddingTop: SPACE.md, display: "flex", flexDirection: "column", gap: SPACE.xs }}>
            {plan.plans.map((p) => (
              <Box key={p.label} sx={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", marginX: "-8px", borderRadius: "8px", "&:hover": { background: P.accentSoft } }}>
                <Typography sx={{ fontSize: TYPE.label, color: P.inkSoft }}>
                  {p.label}
                  {p.show_version && (
                    <Box component="span" sx={{ fontSize: "11px", padding: "0 6px", marginLeft: "6px", borderRadius: "5px", background: P.iconChip, color: P.accent }}>
                      V{p.version}
                    </Box>
                  )}
                </Typography>
                <Typography sx={{ ...NUM, fontSize: TYPE.label, fontWeight: 600, color: P.ink }}>{p.count}</Typography>
              </Box>
            ))}
            {plan.plans.length === 0 && (
              <EmptyState minHeight={96} icon={<WorkspacePremiumOutlined />} title="No active subscriptions yet" text="Plans startups buy will be listed here with how many hold each one." />
            )}
          </Box>
        </Card>
      ),
      assessments: (
        <Card
          title="AI Insights"
          subtitle={assess.subtitle}
          hint="Fundability and pitch-deck reports generated in this range, split by whether the startup unlocked the full result."
          delay={0.515}
          sx={{ padding: { xs: "16px", sm: "24px" }, gap: SPACE.lg }}
          right={<Legend keys={[{ label: "Paid", color: P.accent }, { label: "Free", color: P.pipeline.invited }]} />}
        >
          <DragGrid {...cardGrid(view, "assessments", assess.rows, onItemOrderChange)}>{view.sortItems("assessments", assess.rows).map((r, i) => (<WidgetGate key={r.key} show={view.item("assessments", r.key)}><DragItem id={r.key} label={r.label ?? r.title} radius="14px">
            <Box
              key={r.key}
              className="dv-item"
              style={{ animationDelay: `${0.7 + i * 0.05}s` }}
              sx={{ display: "flex", alignItems: "center", gap: SPACE.lg, paddingY: SPACE.md, borderBottom: i < assess.rows.length - 1 ? `1px solid ${P.line}` : "none" }}
            >
              <CountUp value={r.total} sx={{ fontSize: "2.2rem", fontWeight: 600, letterSpacing: "-0.04em", width: 56, color: P.ink }} />
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Typography sx={{ fontSize: TYPE.label, fontWeight: 500, color: P.ink }}>{r.label}</Typography>
                <Fill ratio={r.total ? r.paid / r.total : 0} color={P.accent} delay={0.8 + i * 0.05} />
                <Typography sx={{ fontSize: TYPE.micro, color: P.inkMuted }}>{r.footnote}</Typography>
              </Box>
            </Box>
          </DragItem></WidgetGate>))}</DragGrid>
          {assess.rows.length === 0 && (
            <EmptyState minHeight={96} icon={<InsightsOutlined />} title="No assessments in this range" text="Fundability and pitch-deck reports startups generate in this range will appear here." />
          )}
        </Card>
      ),
      composition: (
      <Card title="Composition" subtitle={comp.summary} delay={0.58} sx={{ padding: { xs: "16px", sm: "24px" }, gap: SPACE.lg }}>
        <Box sx={tileRow(260, SPACE.xl, shownCount(view, "composition", comp.groups))}>
          <DragGrid {...cardGrid(view, "composition", comp.groups, onItemOrderChange)}>{view.sortItems("composition", comp.groups).map((g, i) => (<WidgetGate key={g.key} fill show={view.item("composition", g.key)}><DragItem id={g.key} label={g.label ?? g.title} radius="14px">
            <Box key={g.key} sx={{ display: "flex", flexDirection: "column", gap: SPACE.sm }}>
              <Typography component="h3" sx={{ margin: 0, fontSize: TYPE.micro, fontWeight: 600, color: P.inkMuted, textTransform: "uppercase", letterSpacing: ".06em" }}>
                {g.title}
              </Typography>
              <RankedBarList rows={g.rows} footer={g.footer} delay={0.75 + i * 0.05} expandable={g.expandable} />
            </Box>
          </DragItem></WidgetGate>))}</DragGrid>
        </Box>
      </Card>
      ),
      campaign_attribution: (
      <Card title="Campaign Attribution" subtitle={camp.summary} delay={0.65} sx={{ padding: { xs: "16px", sm: "24px" }, gap: SPACE.md }}>
        {/* Inside C's own Card, so the listing's border and shadow are turned
            off — otherwise it reads as a card within a card. */}
        <CampaignTable rows={camp.rows} range={data?.range} tableSx={{ border: "none", boxShadow: "none", borderRadius: 0 }} />
      </Card>
      ),
      // Startup platform widgets, shared by every variant.
      ...platformBlocks({ platform, state: platformState, view, onItemOrderChange }),
    };
    // Widgets that span the full row in this design; the rest sit two to a row on wide screens.
    const FULL_WIDTH = ["key_metrics", "pipeline", "daily_activity", "composition", "campaign_attribution", ...PLATFORM_FULL_WIDTH];
    // The widgets on screen, in order: drag targets, and what pairs up two to a row.
    const shown = view.order.filter((k) => blocks[k] && view.widget(k));

  return (
    <DragGrid ids={shown} order={view.order} labels={WIDGET_LABELS} onOrderChange={onOrderChange}>
    <Box sx={{ ...shell, display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }, gap: `${view.gap}px`, alignItems: "stretch", gridAutoFlow: "row dense" }}>
      {view.order.filter((k) => blocks[k]).map((k) => (
        <WidgetGate key={k} fill show={view.widget(k)} style={{ gridColumn: view.span(k, FULL_WIDTH, shown), minWidth: 0 }}>
          <DragItem id={k} label={WIDGET[k]?.label ?? k} handle>{blocks[k]}</DragItem>
        </WidgetGate>
      ))}
    </Box>
    </DragGrid>
  );
};

export default DashboardC;
